import axios from 'axios';
import { User, UpdateProfileData, ChangePasswordData, UserPreferences } from '../types/user';
import { CONFIG } from '../config/constants';

const API_BASE_URL = CONFIG.API.API_URL;

const profileApi = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
profileApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ApiResponse<T> {
  status: string;
  message?: string;
  data?: T;
  errors?: Record<string, string[]>;
}

export const profileService = {
  // Get current user profile
  async getCurrentProfile(): Promise<User> {
    const response = await profileApi.get<ApiResponse<User>>('/profile');
    if (response.data.status === 'success' && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch profile');
  },

  // Update profile information
  async updateProfile(profileData: UpdateProfileData): Promise<User> {
    const response = await profileApi.put<ApiResponse<User>>('/profile', profileData);
    if (response.data.status === 'success' && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update profile');
  },

  // Change password
  async changePassword(passwordData: ChangePasswordData): Promise<void> {
    const response = await profileApi.put<ApiResponse<void>>('/profile/password', passwordData);
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to change password');
    }
  },

  // Upload avatar
  async uploadAvatar(file: File): Promise<{ avatar: string; avatar_url: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await profileApi.post<ApiResponse<{ avatar: string; avatar_url: string }>>(
      '/profile/avatar',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.status === 'success' && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to upload avatar');
  },

  // Delete avatar
  async deleteAvatar(): Promise<void> {
    const response = await profileApi.delete<ApiResponse<void>>('/profile/avatar');
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to delete avatar');
    }
  },

  // Get avatar URL
  async getAvatarUrl(): Promise<string | null> {
    const response = await profileApi.get<ApiResponse<{ avatar_url: string | null }>>('/profile/avatar-url');
    if (response.data.status === 'success' && response.data.data) {
      return response.data.data.avatar_url;
    }
    return null;
  },

  // Update user preferences
  async updatePreferences(preferences: UserPreferences): Promise<UserPreferences> {
    const response = await profileApi.put<ApiResponse<{ preferences: UserPreferences }>>(
      '/profile/preferences',
      { preferences }
    );
    
    if (response.data.status === 'success' && response.data.data) {
      return response.data.data.preferences;
    }
    throw new Error(response.data.message || 'Failed to update preferences');
  },
}; 